import http from "./HttpService";
import lsu from "./LocalStorageUtils";

import { IOTPSession } from "../Types/OTPSession";
import {
  IBankDetailVerificationResponse,
  IDataSetApiResponse,
  IDMACOption,
  IRWBoolResponse,
} from "../Types/AllTypes";
import {
  CreateUpdateNomineeEntryInsertObject,
  GetAccountOpeningDocumentSearchOptions,
  ImpersonateLoginInsertObject,
  PrepareESignRequestInsertObject,
  SetNameEmailInsertObject,
  SetPANDOBInsertObject,
  UpdateAORWithPMLAQuestionAnswersInsertObject,
  UploadAndRegisterAccountOpeningDocumentInsertObject,
} from "../Types/InsertObjects";
import {
  IAOPR,
  IAORProductRelatedDetail,
  IDownloadableDocumentInfo,
  IDownloadResponse,
  IESignRequestResponse,
  IStage3KYCDetail,
  IStage8Info,
  ISubBrokerUser,
} from "../Types/AccountOpeningRequest";
import {
  IAccountOpeningDocumentStatus,
  IAccountOpeningDocumentUploadResponse,
} from "../Types/AOD";
const win = window as any;

class ApiServices {
  withCache(
    apiFunction: any,
    key: string,
    expireDateOrExpireAfterInMinutes: any = null
  ) {
    return async (...args: any[]) => {
      let data = lsu.lsGet(key);
      if (data) {
        return data;
      }
      data = await apiFunction(...args);
      lsu.lsSet(key, data, expireDateOrExpireAfterInMinutes);
      win[key] = data;
      return data;
    };
  }

  withWindowCache(
    apiFunction: any,
    key: string,
    expireDateOrExpireAfterInMinutes: any = null
  ) {
    return async (...args: any[]) => {
      let data = win[key];
      if (data) {
        return data;
      }
      data = await apiFunction(...args);
      win[key] = data;
      return data;
    };
  }

  abortRequest(requestId: string) {
    const win = window as any;
    if (win.requests[requestId] && win.requests[requestId].abort) {
      win.requests[requestId].abort();
      delete win.requests[requestId];
    }
  }

  apiCacheKey = {};

  windowCacheKey = {
    StateList: "StateList",
  };

  clearCache(key: string) {
    lsu.lsSet(key, null);
  }

  clearWindowCache(key: string) {
    win[key] = undefined;
  }

  clearAllCache = () => {
    let apiCacheKeyList: { [key: string]: string } = this.apiCacheKey;
    Object.keys(apiCacheKeyList).forEach((key) => {
      lsu.lsSet(apiCacheKeyList[key], null);
    });

    Object.keys(this.windowCacheKey).forEach((key) => {
      win[key] = undefined;
    });
  };

  async fetchPrefilledRequest(io: {
    AOPRID: number;
    AOPRUID: string;
  }): Promise<IDataSetApiResponse<IAOPR>> {
    let result = await http.googo(10558101, io);
    return result;
  }

  async fetchSubBrokerDetails(io: {
    AOPRID: number;
    AOPRUID: string;
    BPC: string;
  }): Promise<ISubBrokerUser> {
    let result = await http.googo(10558100, io);
    return result;
  }

  async sendOTPToMobile(io: {
    CMN: string;
    AOPRID: number;
    AOPRUID: string;
    BPC: string;
  }): Promise<IOTPSession> {
    let result = await http.googo(10558102, io);
    return result;
  }

  async resendMobileOTP(io: { SOTPR: string }): Promise<IOTPSession> {
    let result = await http.googo(10558103, io);
    return result;
  }

  async verifyMobileOTP(io: {
    MOTP: string;
    SOTPR: string;
  }): Promise<IOTPSession> {
    let result = await http.googo(10558104, io);
    return result;
  }

  async getUnfinishedAOROrAOPR(io: {
    ARAOPR: number;
    AOSAI: string;
  }): Promise<IDataSetApiResponse> {
    let result = await http.googo(10558106, io);
    return result;
  }

  async changeMobileNumber(io: {
    NCMN: string;
    AORID: number;
    AORUID: string;
    AOSAI: string;
  }): Promise<IOTPSession> {
    let result = await http.googo(10558107, io);
    return result;
  }

  async resendChangeMobileOTP(io: {
    SOTPR: string;
    AOSAI: string;
  }): Promise<IOTPSession> {
    let result = await http.googo(10558108, io);
    return result;
  }

  async verifyChangeMobileOTP(io: {
    MOTP: string;
    SOTPR: string;
    AOSAI: string;
  }): Promise<IOTPSession> {
    let result = await http.googo(10558109, io);
    return result;
  }

  async setNameEmail(io: SetNameEmailInsertObject): Promise<IOTPSession> {
    let result = await http.googo(10558110, io);
    return result;
  }
  async verifyEmailOTP(io: {
    EOTP: string;
    SOTPR: string;
    AOSAI: string;
  }): Promise<IOTPSession> {
    let result = await http.googo(10558112, io);
    return result;
  }

  async resendEmailOTP(io: {
    SOTPR: string;
    AOSAI: string;
  }): Promise<IOTPSession> {
    let result = await http.googo(10558111, io);
    return result;
  }

  async setPANDOB(io: SetPANDOBInsertObject): Promise<IOTPSession> {
    let result = await http.googo(10558115, io);
    return result;
  }

  async getDMACOptionList(): Promise<IDataSetApiResponse<IDMACOption>> {
    let result = await http.googo(10558114, {});
    return result;
  }

  // stage 1
  async SendOTPForExistingClient(io: { AOSAI: string }): Promise<IOTPSession> {
    let result = await http.googo(10558116, io);
    return result;
  }

  async ResendOTPForExistingClient(io: {
    AOSAI: string;
    SOTPR: string;
  }): Promise<IOTPSession> {
    let result = await http.googo(10558117, io);
    return result;
  }
  async verifyOTPForExistingClient(io: {
    AOSAI: string;
    SOTPR?: string;
    EACAONA: number;
    EOTP: string;
    MOTP: string;
    BPC?: string;
  }): Promise<IOTPSession> {
    let result = await http.googo(10558118, io);
    return result;
  }

  // stage 2
  async GetAORDetails(io: {
    AOSAI: string;
  }): Promise<IAORProductRelatedDetail> {
    let result = await http.googo(10558120, io);
    return result;
  }

  async setAORProduct(io: {
    AOSAI: string;
    SPICBWs: number;
    SSICBWs: number;
    IDDPICT: string;
  }): Promise<IOTPSession> {
    let result = await http.googo(10558121, io);
    return result;
  }

  // stage 3
  async FetchKYCDetails(io: {
    AOSAI: string;
    ARAOR: number;
  }): Promise<IStage3KYCDetail> {
    let result = await http.googo(10558124, io);
    return result;
  }

  async GetDigiLockerURL(io: { AOSAI: string }): Promise<IRWBoolResponse> {
    let result = await http.googo(10558126, io);
    return result;
  }

  async GetCVLAUAURL(io: { AOSAI: string }): Promise<IRWBoolResponse> {
    let result = await http.googo(10558160, io);
    return result;
  }

  async VerifiedKYCDetails(io: { AOSAI: string }): Promise<IOTPSession> {
    let result = await http.googo(10558127, io);
    return result;
  }

  async GetPLMAQuestions(io: { AOSAI: string }): Promise<IDataSetApiResponse> {
    let result = await http.googo(10558128, io);
    return result;
  }

  async UpdateAORWithPMLAQuestionAnswers(
    io: UpdateAORWithPMLAQuestionAnswersInsertObject
  ): Promise<IOTPSession> {
    let result = await http.googo(10558129, io);
    return result;
  }

  // stage 4/5
  async GetAccountOpeningDocumentDetail(
    io: GetAccountOpeningDocumentSearchOptions
  ): Promise<IDownloadableDocumentInfo> {
    let result = await http.googo(10558130, io); // 10558130 OLD NIK ++
    return result;
  }

  async GetAccountOpeningDocumentList(io: {
    AOSAI: string;
    ILPS: number;
  }): Promise<IDataSetApiResponse> {
    let result = await http.googo(10558131, io); // 10558131 OLD NIK ++
    return result;
  }

  async UploadAndRegisterAccountOpeningDocument(
    io: UploadAndRegisterAccountOpeningDocumentInsertObject
  ): Promise<IAccountOpeningDocumentUploadResponse> {
    let result = await http.googo(10558133, io); // 10558133 OLD NIK ++
    return result;
  }

  async DeleteUploadedDocument(io: {
    AOSAI: string;
    AODID: number;
    AODUID: string;
  }): Promise<IOTPSession> {
    let result = await http.googo(10558132, io); // 10558132 OLD NIK ++
    return result;
  }

  // stage 7
  async ReturnExistingNomineeList(io: { AOSAI: string }): Promise<IOTPSession> {
    let result = await http.googo(10558145, io);
    return result;
  }

  async CreateNomineeEntry(
    io: CreateUpdateNomineeEntryInsertObject
  ): Promise<IOTPSession> {
    let result = await http.googo(10558146, io);
    return result;
  }

  async UpdateNomineeEntry(
    io: CreateUpdateNomineeEntryInsertObject
  ): Promise<IOTPSession> {
    let result = await http.googo(10558147, io);
    return result;
  }

  async DeleteNomineeEntry(io: {
    AOSAI: string;
    AORXNID: number;
    AORXNUID: string;
  }): Promise<IOTPSession> {
    let result = await http.googo(10558148, io);
    return result;
  }

  async SaveNomineeDetails(io: {
    AOSAI: string;
    HNN: number;
  }): Promise<IOTPSession> {
    let result = await http.googo(10558149, io);
    return result;
  }

  //stage 6

  async GetStage6Info(io: { AOSAI: string }): Promise<IOTPSession> {
    let result = await http.googo(10558140, io);
    return result;
  }

  async GetMICRList(io: {
    AOSAI: string;
    IFSC: string;
  }): Promise<IDataSetApiResponse> {
    let result = await http.googo(10558141, io);
    return result;
  }

  async SubmitBankAccountDetail(io: {
    AOSAI: string;
    CBAN: string;
    CBIFSC: string;
    CBMICR: string;
    CBATIC: number;
    MIS?: string;
    MBN?: string;
  }): Promise<IOTPSession> {
    let result = await http.googo(10558142, io);
    return result;
  }

  async CheckBankDetailStatus(io: {
    AOSAI: string;
    PN: number;
  }): Promise<IBankDetailVerificationResponse> {
    let result = await http.googo(10558143, io);
    return result;
  }

  async CheckDocumentStatus(io: {
    AODID: number;
    AODUID: string;
    PN: number;
  }): Promise<IAccountOpeningDocumentStatus> {
    let result = await http.googo(10558134, io);
    return result;
  }

  async PrepareESignDocumentRequest(
    io: PrepareESignRequestInsertObject
  ): Promise<IESignRequestResponse> {
    let result = await http.googo(10558150, io);
    return result;
  }

  async ValidateUserIsEligibleToDoESign(io: {
    AOSAI: string;
  }): Promise<IStage8Info> {
    let result = await http.googo(10558151, io);
    return result;
  }

  async DownloadSignedAOFDocumentByAnnonymousUser(io: {
    AOSAI: string;
  }): Promise<IDownloadResponse> {
    let result = await http.googo(10558152, io);
    return result;
  }

  async DownloadAccountOpeningFormDraftDocument(io: {
    AOSAI: string;
  }): Promise<IDownloadResponse> {
    let result = await http.googo(10558153, io);
    return result;
  }

  async GetQueryList(io: {
    AORID: number;
    AORUID: string;
  }): Promise<IDataSetApiResponse> {
    let result = await http.googo(10558105, io);
    return result;
  }

  async DiscardApplication(io: { AOSAI: string }): Promise<IOTPSession> {
    let result = await http.googo(10558159, io);
    return result;
  }

  async ChangeDOB(io: {
    AOSAI: string;
    NDOB: Date | null;
  }): Promise<IStage3KYCDetail> {
    let result = await http.googo(10558125, io);
    return result;
  }

  async ImpersonateLogin(
    io: ImpersonateLoginInsertObject
  ): Promise<IOTPSession> {
    let result = await http.googo(10558113, io);
    return result;
  }

  async SaveACHRegistrationRequestWithAOSUseCase(io: {
    AOSAI: string;
    AMLA: number;
    AMVFD: Date;
    AMVTD: Date;
  }): Promise<IRWBoolResponse> {
    let result = await http.googo(10558155, io);
    return result;
  }
}

const api = new ApiServices();
(window as any)["api"] = api;
export default api;
